import {
  FOUNDRY_CONTROL_PANEL_HUB,
  findFoundryControlPanelGroup,
  findFoundryControlPanelItem,
} from "~/lib/foundry-nav";
import { findSettingsGroup, findSettingsItem } from "~/lib/settings-nav";

export type BreadcrumbSegment = { label: string; to?: string };

export type BreadcrumbSpec = {
  segments: BreadcrumbSegment[];
  appendPageTitle?: boolean;
  appendChatTitle?: boolean;
};

const AI_VISIBILITY_HUB = "/ai-visibility/home";
const SETTINGS_HUB = "/settings/workspace/general";
const FOUNDRY_HUB = "/foundry";
const AGENCY_HOME = "/agency/home";
const AGENCY_SETTINGS_HUB = "/agency/settings/general";

export function getBreadcrumbSpec(
  pathname: string,
  options?: { isAgency?: boolean }
): BreadcrumbSpec | null {
  if (pathname === "/agency/home") {
    return { segments: [{ label: "Clients" }] };
  }

  if (pathname === "/agency/people") {
    return { segments: [{ label: "People" }] };
  }

  if (pathname === "/agency/clients/new") {
    return {
      segments: [
        { label: "Clients", to: AGENCY_HOME },
        { label: "Add client" },
      ],
    };
  }

  if (pathname === "/agency/settings/general") {
    return {
      segments: [
        { label: "Settings", to: AGENCY_SETTINGS_HUB },
        { label: "General" },
      ],
    };
  }

  if (pathname === "/agency/settings/billing") {
    return {
      segments: [
        { label: "Settings", to: AGENCY_SETTINGS_HUB },
        { label: "Billing" },
      ],
    };
  }

  if (pathname === "/agency/settings/billing/subscribe") {
    return {
      segments: [
        { label: "Settings", to: AGENCY_SETTINGS_HUB },
        { label: "Billing", to: "/agency/settings/billing" },
        { label: "Subscribe" },
      ],
    };
  }

  if (pathname === "/agency/settings/entitlements") {
    return {
      segments: [
        { label: "Settings", to: AGENCY_SETTINGS_HUB },
        { label: "Entitlements" },
      ],
    };
  }

  const clientEntitlementsMatch = pathname.match(
    /^\/agency\/settings\/entitlements\/(\d+)$/
  );
  if (clientEntitlementsMatch) {
    return {
      segments: [
        { label: "Settings", to: AGENCY_SETTINGS_HUB },
        { label: "Entitlements", to: "/agency/settings/entitlements" },
      ],
      appendPageTitle: true,
    };
  }

  if (pathname === "/chats/home") {
    return { segments: [{ label: "Zerply Agent" }] };
  }

  const chatMatch = pathname.match(/^\/chats\/(\d+)$/);
  if (chatMatch) {
    return {
      segments: [{ label: "Zerply Agent", to: "/chats/home" }],
      appendChatTitle: true,
    };
  }

  const promptCitationsMatch = pathname.match(
    /^\/ai-visibility\/prompts\/(\d+)\/citations$/
  );
  if (promptCitationsMatch) {
    return {
      segments: [
        { label: "AI Visibility", to: AI_VISIBILITY_HUB },
        { label: "Prompts", to: "/ai-visibility/prompts" },
        { label: "Citations" },
      ],
    };
  }

  const promptDetailMatch = pathname.match(/^\/ai-visibility\/prompts\/(\d+)$/);
  if (promptDetailMatch) {
    return {
      segments: [
        { label: "AI Visibility", to: AI_VISIBILITY_HUB },
        { label: "Prompts", to: "/ai-visibility/prompts" },
      ],
      appendPageTitle: true,
    };
  }

  if (pathname === "/ai-visibility/home") {
    return {
      segments: [
        { label: "AI Visibility", to: AI_VISIBILITY_HUB },
        { label: "Home" },
      ],
    };
  }

  if (
    pathname === "/ai-visibility/prompts" ||
    pathname === "/ai-visibility/prompts/suggestions" ||
    pathname === "/ai-visibility/prompts/removals"
  ) {
    return {
      segments: [
        { label: "AI Visibility", to: AI_VISIBILITY_HUB },
        { label: "Prompts" },
      ],
    };
  }

  if (pathname.startsWith("/ai-visibility/sources")) {
    return {
      segments: [
        { label: "AI Visibility", to: AI_VISIBILITY_HUB },
        { label: "Sources" },
      ],
    };
  }

  if (
    pathname === "/ai-visibility/signals" ||
    pathname.startsWith("/ai-visibility/signals/")
  ) {
    return {
      segments: [
        { label: "AI Visibility", to: AI_VISIBILITY_HUB },
        { label: "Signals", to: "/ai-visibility/signals" },
      ],
    };
  }

  if (pathname === "/ai-visibility/citations") {
    return {
      segments: [
        { label: "AI Visibility", to: AI_VISIBILITY_HUB },
        { label: "Citations" },
      ],
    };
  }

  if (pathname === "/ai-visibility/ai-traffic-analytics") {
    return {
      segments: [
        { label: "AI Visibility", to: AI_VISIBILITY_HUB },
        { label: "AI Traffic Analytics" },
      ],
    };
  }

  const llmResponseMatch = pathname.match(
    /^\/ai-visibility\/responses\/(\d+)$/
  );
  if (llmResponseMatch) {
    return {
      segments: [
        { label: "AI Visibility", to: AI_VISIBILITY_HUB },
        { label: "Response" },
      ],
      appendPageTitle: true,
    };
  }

  if (pathname === "/shopping") {
    return {
      segments: [
        { label: "AI Visibility", to: AI_VISIBILITY_HUB },
        { label: "Shopping" },
      ],
    };
  }

  if (/^\/shopping\/products\/\d+$/.test(pathname)) {
    return {
      segments: [
        { label: "AI Visibility", to: AI_VISIBILITY_HUB },
        { label: "Shopping", to: "/shopping" },
      ],
      appendPageTitle: true,
    };
  }

  if (pathname === "/keyword-clusters") {
    return { segments: [{ label: "Keyword Clusters" }] };
  }

  if (pathname === "/library") {
    return { segments: [{ label: "Library" }] };
  }

  const clusterReportMatch = pathname.match(/^\/keyword-clusters\/(\d+)$/);
  if (clusterReportMatch) {
    return {
      segments: [
        { label: "Keyword Clusters", to: "/keyword-clusters" },
        { label: "Report" },
      ],
    };
  }

  if (pathname === "/blogs") {
    return { segments: [{ label: "Blogs" }] };
  }

  const legacyBlogMatch = pathname.match(/^\/blogs\/(\d+)$/);
  if (legacyBlogMatch) {
    return {
      segments: [{ label: "Blogs", to: "/blogs" }],
      appendPageTitle: true,
    };
  }

  if (pathname === "/foundry/blogs") {
    return {
      segments: [{ label: "Foundry", to: FOUNDRY_HUB }, { label: "Blogs" }],
    };
  }

  const foundryBlogMatch = pathname.match(/^\/foundry\/blogs\/(\d+)$/);
  if (foundryBlogMatch) {
    return {
      segments: [
        { label: "Foundry", to: FOUNDRY_HUB },
        { label: "Blogs", to: "/foundry/blogs" },
      ],
      appendPageTitle: true,
    };
  }

  if (pathname === "/foundry/landing-pages") {
    return {
      segments: [
        { label: "Foundry", to: FOUNDRY_HUB },
        { label: "Landing Pages" },
      ],
    };
  }

  const foundryLpMatch = pathname.match(/^\/foundry\/landing-pages\/(\d+)$/);
  if (foundryLpMatch) {
    return {
      segments: [
        { label: "Foundry", to: FOUNDRY_HUB },
        { label: "Landing Pages", to: "/foundry/landing-pages" },
      ],
      appendPageTitle: true,
    };
  }

  if (pathname === "/foundry/tools") {
    return {
      segments: [{ label: "Foundry", to: FOUNDRY_HUB }, { label: "Tools" }],
    };
  }

  const foundryToolMatch = pathname.match(/^\/foundry\/tools\/(\d+)$/);
  if (foundryToolMatch) {
    return {
      segments: [
        { label: "Foundry", to: FOUNDRY_HUB },
        { label: "Tools", to: "/foundry/tools" },
      ],
      appendPageTitle: true,
    };
  }

  if (pathname === "/foundry/lead-forms") {
    return {
      segments: [
        { label: "Foundry", to: FOUNDRY_HUB },
        { label: "Lead Forms" },
      ],
    };
  }

  const foundryLeadFormMatch = pathname.match(/^\/foundry\/lead-forms\/(\d+)$/);
  if (foundryLeadFormMatch) {
    return {
      segments: [
        { label: "Foundry", to: FOUNDRY_HUB },
        { label: "Lead Forms", to: "/foundry/lead-forms" },
      ],
      appendPageTitle: true,
    };
  }

  if (
    pathname === "/foundry/control-panel" ||
    pathname.startsWith("/foundry/control-panel/")
  ) {
    const item = findFoundryControlPanelItem(pathname);
    const group = findFoundryControlPanelGroup(pathname);
    return {
      segments: [
        { label: "Foundry", to: FOUNDRY_HUB },
        { label: "Control Panel", to: FOUNDRY_CONTROL_PANEL_HUB },
        ...(item
          ? [
              ...(group && group.title !== "Settings" && group.title !== "Setup"
                ? [{ label: group.title }]
                : []),
              { label: item.title },
            ]
          : []),
      ],
    };
  }

  if (pathname === "/profile") {
    if (options?.isAgency) {
      return { segments: [{ label: "Profile" }] };
    }
    return {
      segments: [{ label: "Settings", to: SETTINGS_HUB }, { label: "Profile" }],
    };
  }

  if (pathname === "/mcp") {
    return { segments: [{ label: "MCP" }] };
  }

  const settingsItem = findSettingsItem(pathname);
  if (settingsItem) {
    const group = findSettingsGroup(pathname);
    const isDetail = settingsItem.path !== pathname;
    return {
      segments: [
        { label: "Settings", to: SETTINGS_HUB },
        ...(group ? [{ label: group.title }] : []),
        isDetail
          ? { label: settingsItem.title, to: settingsItem.path }
          : { label: settingsItem.title },
      ],
      appendPageTitle: isDetail,
    };
  }

  if (pathname === "/settings/workspace/billing/subscribe") {
    return {
      segments: [
        { label: "Settings", to: SETTINGS_HUB },
        { label: "Billing", to: "/settings/workspace/billing" },
        { label: "Subscribe" },
      ],
    };
  }

  return null;
}
